import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import StackButton from "@/components/buttons/StackButton";
import { FormPageNameField } from "@/components/forms/FormPageNameField";
import StatusToggle from "@/components/toggles/StatusToggle";
import { DEFAULT_FORM_TOGGLE_ROW_COUNT, ToggleListSkeleton } from "@/components/skeletons";
import { saveRole } from '@/services/role.service';
import { getFeaturesListFromAPI } from "@/services/setting.service";
import { BUTTONS, HEADINGS, LABELS } from "@/utils/constants/appConstants/commonConstants";
import { ROLE_CREATED_SUCCESSFULLY, errorMessages } from '@/utils/constants/messages';
import { storeInLocalStorage } from "@/utils/functions/helperFunctions";
import { errorToasterMessage, successToasterMessage } from '@/utils/functions/toaster';


const CreateRole = () => {
    const [roleName, setRoleName] = React.useState("");
    const [allFeatures, setAllFeatures] = React.useState<any>([]);
    const [checkedCount, setCheckedCount] = React.useState(0);
    const [loader, setLoader] = React.useState(false);

    const navigate = useNavigate();

    const handleSaveRole = () => {
        setLoader(true);
        const newFeatureList = allFeatures.filter((feature: any) => {
            return feature.checked
        }).map((feature: any) => {
            return feature.name
        });
        if (newFeatureList.length === 0) {
            errorToasterMessage(errorMessages.SELECT_ANY_ONE_FEATURE)
            setLoader(false);
            return
        }
        const data = JSON.stringify({
            name: roleName,
            features: newFeatureList,
        });
        saveRole(data, () => {
            successToasterMessage(ROLE_CREATED_SUCCESSFULLY)
            setLoader(false);
            navigate(-1);
        }, (error: any) => {
            setLoader(false);
            console.log(error);
            errorToasterMessage(error.data.message)
        });
    };

    useEffect(() => {
        getFeaturesListFromAPI()
            .then((response: any) => {
                storeInLocalStorage(allFeatures, response.data.features);
                const new_features_list = response.data.features.map((feature: { checked: boolean }) => {
                    feature.checked = false;
                    return feature;
                });
                setAllFeatures(new_features_list);
            })
            .catch((e) => {
                console.log(e);
            });
    }, []);

    const onChangeFeature = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newFeatureList = allFeatures.map((feature: any) => {
            if (e.target.name === feature.name) {
                const new_feature = { ...feature, checked: e.target.checked };
                setCheckedCount(checkedCount + (e.target.checked ? 1 : -1));
                return new_feature;
            }
            return feature;
        });
        setAllFeatures(newFeatureList);
    };

    const onChangeName = (e: React.ChangeEvent<HTMLInputElement>) => {
        setRoleName(e.target.value);
    };

    const isSaveDisabled = checkedCount === 0 || !roleName.trim();

    return (
        <div className="gs-form-page">
            <div className="gs-form-page-header">
                <h3 className="gs-form-page-title">
                    <strong id="create-role-title">{HEADINGS.CREATE_ROLE}</strong>
                </h3>
                <StackButton
                    secondaryVariant="light"
                    primaryId="create-role-save-btn"
                    secondaryId="create-role-cancel-btn"
                    primaryButton={BUTTONS.SAVE}
                    secondaryButton={BUTTONS.CANCEL}
                    handlePrimary={handleSaveRole}
                    handleSecondary={() => navigate(-1)}
                    disabled={isSaveDisabled}
                    isLoading={loader}
                />
            </div>

            <form
                onSubmit={(event) => {
                    event.preventDefault();
                    if (!isSaveDisabled && !loader) {
                        handleSaveRole();
                    }
                }}
            >
                <FormPageNameField
                    label={LABELS.ROLE_NAME}
                    labelId="create-role-name-label"
                    inputId="create-role-name-input"
                    autoFocus
                    value={roleName}
                    onChange={onChangeName}
                />

                <div className="gs-form-page-section">
                    <h5 className="gs-form-section-title gs-form-section-title--spaced" id="create-role-permission-label">
                        {HEADINGS.PERMISSIONS}
                    </h5>
                    <div className="gs-list-page-card">
                        <ul className="gs-detail-list gs-form-toggle-list">
                            {allFeatures.length > 0 ? (
                                allFeatures.map((feature: any, key: number) => (
                                    <li className="gs-detail-list-item" key={feature.name}>
                                        <StatusToggle
                                            index={key}
                                            onChange={onChangeFeature}
                                            name={feature.name as string}
                                            id={feature.name as string}
                                            checked={feature.checked}
                                        >
                                            <p className="m-0" id={`create-role-permission-feature-name-${key}`}>{feature.name}</p>
                                        </StatusToggle>
                                    </li>
                                ))
                            ) : (
                                <ToggleListSkeleton rowCount={DEFAULT_FORM_TOGGLE_ROW_COUNT} />
                            )}
                        </ul>
                    </div>
                </div>
            </form>
        </div>
    );
};

export default CreateRole;
